import { saveAttendance } from "@/api/openapi";
import { AttendancesContext, PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import { useContext, useState } from "react";
import { toast } from "react-toastify";
import {
  attendanceToTempAttendance,
  removeVoceFromAttendance,
} from "../../../attendanceUtils";

type UseAttendanceMobileEmployeeVociTabParams = {
  employeeMatricola: number;
  employeeFiliale: number;
};

export function useAttendanceMobileEmployeeVociTab({
  employeeMatricola,
  employeeFiliale,
}: UseAttendanceMobileEmployeeVociTabParams) {
  const { lang, selectedMandateId } = useContext(PspContext);
  const {
    setAttendanceMonth,
    setTempAttendanceMonth,
    attendanceMonth,
    selectedMonth,
    selectedYear,
  } = useContext(AttendancesContext);

  const [aboutToRemoveVoceIndex, setAboutToRemoveVoceIndex] = useState<
    number | null
  >(null);
  const [removeVoceConfirmationOpen, setRemoveVoceConfirmationOpen] =
    useState(false);
  const [addVoceBottomSheetOpen, setAddVoceBottomSheetOpen] = useState(false);

  const attendance =
    attendanceMonth?.attendances.find(
      (a) =>
        a.employee.matricola === employeeMatricola &&
        a.employee.filiale === employeeFiliale,
    ) ?? null;

  const aboutToRemoveVoce =
    aboutToRemoveVoceIndex !== null
      ? (attendance?.voci[aboutToRemoveVoceIndex] ?? null)
      : null;

  function onOpenRemoveVoceDialog(voceIndex: number) {
    setRemoveVoceConfirmationOpen(true);
    setAboutToRemoveVoceIndex(voceIndex);
  }

  function onCloseRemoveVoceDialog() {
    setRemoveVoceConfirmationOpen(false);
    setTimeout(() => {
      setAboutToRemoveVoceIndex(null);
    }, 250);
  }

  function onRemoveVoce() {
    if (!attendanceMonth) {
      toast(translate("somethingWentWrong", lang));
      throw new Error("Tried to remove voce but attendanceMonth is undefined.");
    }
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }
    if (aboutToRemoveVoceIndex === null) {
      toast(translate("somethingWentWrong", lang));
      throw new Error(
        "Tried to remove voce but aboutToRemoveVoceIndex is undefined.",
      );
    }
    const newAttendance = removeVoceFromAttendance(
      attendanceMonth,
      employeeMatricola,
      employeeFiliale,
      aboutToRemoveVoceIndex,
    );

    saveAttendance({
      body: {
        data: newAttendance.attendances,
        mandateId: selectedMandateId,
        month: selectedMonth,
        year: selectedYear,
      },
      query: {
        draft: true,
      },
    }).then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
        throw error;
      }
      onCloseRemoveVoceDialog();
      toast(translate("voceRemovedSuccessfully", lang));
      setTimeout(() => {
        setAttendanceMonth(data);
        setTempAttendanceMonth(attendanceToTempAttendance(data));
      }, 250);
    });
  }

  return {
    onRemoveVoce,
    onOpenRemoveVoceDialog,
    removeVoceConfirmationOpen,
    aboutToRemoveVoce,
    onCloseRemoveVoceDialog,
    attendance,
    lang,
    addVoceBottomSheetOpen,
    setAddVoceBottomSheetOpen,
  };
}
